import { validationStrings } from '@strata/strata-ui-strings';
import { getIn } from 'formik';
import * as Yup from 'yup';

import {
    ActivationTypes,
    convertYupPathToFormikPath,
    FundingSourcesForDisplay,
    SubTaskConditionTypes,
} from 'components/valueCase/helpers';

const requiredErrorMessage = validationStrings.requiredField.errorMessage;

export const yupNumber = Yup.number()
    .transform((value) => (Number.isNaN(value) ? null : value))
    .nullable();

export const handleNumericValidation = (formObject, baseSchema) => {
    const validation = {
        min: 0,
        max: undefined,
        isInteger: true,
        ...formObject?.numericValidation,
    };
    let schema = baseSchema;
    if (validation.min != null) {
        schema = schema.min(
            validation.min,
            `Value must be greater than or equal to ${validation.min}.`,
        );
    }
    if (validation.max != null) {
        schema = schema.max(
            validation.max,
            `Value must be less than or equal to ${validation.max}.`,
        );
    }
    if (validation.isInteger != null && validation.isInteger === true) {
        schema = schema.integer('Must be a whole number.');
    }
    return schema;
};

export const yupNumberMinZero = yupNumber.min(0, 'Value must be greater than or equal to 0.');

export const conditionTypeValidationMessage = 'Requirement component type must match subtask type.';

export const valueCaseValidationSchema = Yup.object({
    payer_id: Yup.string().required(requiredErrorMessage),
    value_case_name: Yup.string()
        .max(50, validationStrings.maxCharacter[50])
        .required(requiredErrorMessage),
    funding_source: Yup.string()
        .oneOf(Object.keys(FundingSourcesForDisplay))
        .required(requiredErrorMessage),
    product_service_incentives: Yup.array().required(requiredErrorMessage),
    diagnoses: Yup.array().of(
        Yup.object({
            dx_oid: Yup.string(),
        }),
    ),
    start_date: Yup.date().required(requiredErrorMessage),
    end_date: Yup.date().required(requiredErrorMessage),
    strat_rule_id: Yup.string().required(requiredErrorMessage),
    performance_requirement_name: Yup.string()
        .max(255, validationStrings.maxCharacter[255])
        .required(requiredErrorMessage),
    value_case_description: Yup.string()
        .max(1000, validationStrings.maxCharacter.lambda(1000))
        .required(requiredErrorMessage),
    incentive_amt: yupNumberMinZero.required(requiredErrorMessage),
    performance_days: yupNumberMinZero.required(requiredErrorMessage),
    evaluation_days: yupNumberMinZero.required(requiredErrorMessage),
    min_performance_days: yupNumberMinZero.required(requiredErrorMessage),
    min_claim_count: yupNumberMinZero.required(requiredErrorMessage),
    tasks: Yup.array()
        .of(
            Yup.object({
                task_name: Yup.string()
                    .max(100, validationStrings.maxCharacter[100])
                    .required(requiredErrorMessage),
                activation_type: Yup.string().required(requiredErrorMessage),
                on_day_days: yupNumberMinZero.when('activation_type', ([activationType], schema) =>
                    activationType === ActivationTypes.ON_DAY
                        ? schema.required(validationStrings.requiredField.errorMessage)
                        : schema,
                ),
                subtasks: Yup.array()
                    .of(
                        Yup.object({
                            subtask_title: Yup.string()
                                .max(100, validationStrings.maxCharacter[100])
                                .required(requiredErrorMessage),
                            subtask_instructions: Yup.string()
                                .max(500, validationStrings.maxCharacter[500])
                                .required(requiredErrorMessage),
                            subtask_type: Yup.string()
                                .oneOf(Object.values(SubTaskConditionTypes))
                                .required(requiredErrorMessage)
                                .test({
                                    name: 'check-condition-types',
                                    message:
                                        'One or more conditions do not match this subtask type. Please correct the subtask type, or the conditions.',
                                    test: (value, context) => {
                                        const subtaskTypePathElems = convertYupPathToFormikPath(
                                            context.path,
                                        ).split('.');
                                        const subtaskPath = subtaskTypePathElems
                                            .slice(0, subtaskTypePathElems.length - 1)
                                            .join('.');
                                        const conditionsPath = `${subtaskPath}.conditions`;
                                        const conditions =
                                            getIn(context.options.context, conditionsPath) ?? [];
                                        const conditionIsValid = (cond) =>
                                            cond?.condition_type == null ||
                                            cond?.condition_type === value;
                                        return conditions.every(conditionIsValid);
                                    },
                                    exclusive: true,
                                }),
                            completion_days: yupNumberMinZero.when(
                                'subtask_type',
                                ([subtaskType], schema) =>
                                    subtaskType === SubTaskConditionTypes.MANUAL
                                        ? schema.required(
                                              validationStrings.requiredField.errorMessage,
                                          )
                                        : schema,
                            ),
                            warning_threshold_days: yupNumberMinZero.when(
                                'subtask_type',
                                ([subtaskType], schema) =>
                                    subtaskType === SubTaskConditionTypes.MANUAL
                                        ? schema.required(
                                              validationStrings.requiredField.errorMessage,
                                          )
                                        : schema,
                            ),
                            conditions: Yup.array()
                                .of(
                                    Yup.object({
                                        condition_title: Yup.string()
                                            .max(255, validationStrings.maxCharacter[255])
                                            .required(requiredErrorMessage),
                                        condition_type: Yup.string()
                                            .oneOf(Object.values(SubTaskConditionTypes))
                                            .test({
                                                name: 'check-condition-type',
                                                message: conditionTypeValidationMessage,
                                                test: (value, context) => {
                                                    if (value == null) {
                                                        return true;
                                                    }
                                                    const conditionTypePathElems =
                                                        convertYupPathToFormikPath(
                                                            context.path,
                                                        ).split('.');
                                                    const subtaskPath = conditionTypePathElems
                                                        .slice(0, conditionTypePathElems.length - 3)
                                                        .join('.');
                                                    const subtaskType = getIn(
                                                        context.options.context,
                                                        `${subtaskPath}.subtask_type`,
                                                    );
                                                    return subtaskType === value;
                                                },
                                                exclusive: true,
                                            }),
                                        condition_name: Yup.string().required(requiredErrorMessage),
                                        params: Yup.object({}),
                                    }),
                                )
                                .min(1)
                                .required(requiredErrorMessage),
                        }),
                    )
                    .min(1),
            }),
        )
        .min(1),
});
